import Marquee from "react-fast-marquee";
import { ArrowRight, Star, Plus, X as XIcon, MapPin, Check, HeartHandshake } from "lucide-react";
import Nav from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import PassportModal from "@/components/PassportModal";
import { MembershipCTA } from "@/components/MembershipCheckout";
import { FAQS_DATA } from "@/content/faqs";
import { useState, useEffect } from "react";

const DUO_IMG = "/hero-duo.webp";

const Sticker = ({ children, dark, className = "" }) => (
  <span
    className={`inline-flex items-center gap-1.5 font-bold text-xs uppercase tracking-wide px-4 py-2 rounded-full border-2 border-ink ${
      dark ? "bg-ink text-cream" : "bg-sage text-ink"
    } ${className}`}
  >
    {children}
  </span>
);

const Eyebrow = ({ children, light }) => (
  <span className={`inline-block font-semibold text-xs uppercase tracking-[0.2em] mb-5 ${light ? "text-sage" : "text-[#A85440]"}`}>
    {children}
  </span>
);

/* ── Sections ─────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section id="top" className="relative pt-24 sm:pt-32 md:pt-40 pb-14 sm:pb-16 md:pb-24">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-[1.08fr_0.92fr] gap-8 lg:gap-12 items-center">
        <div className="hero-copy">
          <span className="animate-rise block text-xs font-semibold uppercase tracking-[0.18em] text-[#A85440] mb-3">SAEBIT / SKIN · Bandar Sunway</span>
          <h1 className="animate-rise font-disp font-extrabold tracking-[-0.04em] leading-[0.94] sm:leading-[0.92] text-ink text-[2.65rem] sm:text-6xl lg:text-7xl mt-4 sm:mt-6 mb-4 sm:mb-6" style={{ animationDelay: "0.07s" }}>
            Your skin is talking.
            <br /><span className="serif-it text-coralDeep">Learn what it needs.</span>
          </h1>
          <p className="animate-rise text-base sm:text-lg md:text-xl text-mutedink max-w-md leading-relaxed mb-5 sm:mb-6" style={{ animationDelay: "0.14s" }}>
            Discover what's really holding your skin back in our free <strong className="text-ink font-semibold">90-second Skin Assessment</strong>.
          </p>
          <p className="animate-rise text-sm font-semibold uppercase tracking-[0.12em] text-mutedink mb-3" style={{ animationDelay: "0.17s" }}>In 90 seconds you get:</p>
          <ul className="animate-rise space-y-4 max-w-md mb-4" style={{ animationDelay: "0.18s" }} data-testid="hero-benefits">
            {[
              ["Your skin read.", "What your skin is actually doing right now, in plain words. Built on the same reading system our studio uses."],
              ["Your one blocker.", "The single thing holding your skin back first. One priority, so you know where to start."],
              ["Your starter training plan.", "Cleanse, hydrate, SPF and room for one gentle active, the ingredient is chosen at your scan, gated to your age."],
            ].map(([b, rest]) => (
              <li key={b} className="flex items-start gap-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-coral/15 shrink-0 mt-0.5">
                  <Check size={13} strokeWidth={3} className="text-clay" />
                </span>
                <span className="leading-snug">
                  <strong className="block font-semibold text-ink text-base sm:text-[17px]">{b}</strong>
                  <span className="text-sm text-mutedink leading-relaxed">{rest}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="animate-rise text-sm serif-it text-mutedink mb-7" style={{ animationDelay: "0.19s" }} data-testid="hero-scan-kicker">Want the numbers? That's what the scan is for.</p>
          <div className="animate-rise flex flex-wrap items-center gap-x-5 gap-y-2.5 mb-7 sm:mb-8" style={{ animationDelay: "0.21s" }}>
            <a
              href="/quiz"
              className="group inline-flex items-center gap-2 bg-clay text-white font-semibold text-base sm:text-lg whitespace-nowrap px-5 sm:px-7 py-4 rounded-full border-2 border-ink shadow-[4px_4px_0_#3D2B24] hover:-translate-y-[2px] hover:shadow-[7px_7px_0_#3D2B24] active:translate-y-0 active:shadow-none transition-all"
              data-testid="hero-cta-quiz"
            >
              Get my skin read
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <span className="text-sm text-lightink max-w-[16rem]">90 seconds · no products to buy</span>
          </div>
          <p className="animate-rise text-sm text-mutedink max-w-md mb-7 sm:mb-8" style={{ animationDelay: "0.25s" }} data-testid="hero-trust-line">
            Built on a methodology from Mikwang Academy in Seoul, trusted by hundreds of clients improving their skin through structured skin training.
          </p>
          <div className="animate-rise" style={{ animationDelay: "0.28s" }}>
            <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-lightink mb-2.5">Tap what sounds like you</span>
            <div className="flex flex-wrap gap-2.5">
              {[
                { label: "oily + dry at once?", slug: "combo" },
                { label: "breakouts that won't quit?", slug: "breakouts" },
                { label: "marks that linger?", slug: "marks" },
                { label: "just wanna keep good skin?", slug: "maintain" },
              ].map((c) => (
                <a
                  key={c.slug}
                  href={`/quiz?concern=${c.slug}`}
                  data-testid={`hero-chip-${c.slug}`}
                  className="text-sm font-medium px-4 py-2 border-[1.5px] border-ink/20 rounded-full bg-paper text-mutedink hover:border-coral hover:text-ink hover:-translate-y-[1px] hover:shadow-[2px_2px_0_#D48F7D] transition-all cursor-pointer"
                >
                  {c.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Visual */}
        <div className="relative min-h-[330px] sm:min-h-[420px] lg:min-h-[460px] flex items-center justify-center mt-2 lg:mt-0" aria-hidden="true">
          <div className="blob absolute inset-y-[4%] left-[8%] right-[-4%] opacity-90 animate-blob" />
          <img
            src={DUO_IMG}
            srcSet="/hero-duo-480.webp 480w, /hero-duo.webp 848w"
            sizes="(max-width: 640px) 78vw, 430px"
            alt="Southeast Asian teenagers with clear, healthy skin"
            width="848"
            height="1264"
            fetchPriority="high"
            decoding="async"
            className="relative z-10 w-[78%] sm:w-[80%] max-w-[300px] sm:max-w-[430px] aspect-[4/5] object-cover rounded-[18px] border-[2.5px] border-ink shadow-[6px_6px_0_#3D2B24] sm:shadow-[9px_9px_0_#3D2B24]"
          />
          <div className="absolute z-20 left-1/2 -translate-x-1/2 bottom-[-14px] flex flex-wrap sm:flex-nowrap justify-center items-center gap-x-2 sm:gap-x-2.5 gap-y-1 bg-cream border-2 border-ink rounded-full px-3.5 sm:px-4 py-2 shadow-[3px_3px_0_#3D2B24] max-w-[92%] sm:max-w-none sm:whitespace-nowrap text-center">
            <span className="text-xs font-bold uppercase tracking-wide text-ink">Korean method</span>
            <span className="hidden sm:inline w-1 h-1 rounded-full bg-coral" />
            <span className="text-xs font-bold uppercase tracking-wide text-mutedink">Adapted for young tropical skin</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Ribbon() {
  const items = [
    "Train your skin, don't just treat it",
    "Read the pattern",
    "Condition, don't quick-fix",
    "Skinmaxxing, but measured",
    "Progress that compounds",
  ];
  return (
    <div className="hidden sm:block bg-ink border-y-[2.5px] border-ink py-3.5 overflow-hidden">
      <Marquee speed={45} gradient={false}>
        {items.concat(items).map((t, i) => (
          <span key={`${t}-${i}`} className="font-disp font-bold uppercase text-cream text-base tracking-wide mx-6">
            {t} <span className="text-coral">•</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

const CAMPUSES = ["Sunway University", "Monash Malaysia", "Taylor's", "INTI", "Sunway College"];

function Story() {
  return (
    <section id="what" className="bg-paper py-16 sm:py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <Reveal>
          <Eyebrow>What saebit is</Eyebrow>
          <p className="font-disp font-bold tracking-[-0.03em] leading-[1.04] text-ink text-3xl md:text-[3rem] max-w-[24ch]">
            Bodies change. <span className="serif-it text-coralDeep">What they need</span> changes too.
          </p>
          <p className="text-lg text-mutedink max-w-[48ch] mt-7 leading-relaxed">
            Your skin changes fast when you're young. Oil changes. Breakouts start. Products that worked stop working. And nobody really gives you the manual. That's where we're starting.
          </p>
          <p className="text-lg text-mutedink max-w-[48ch] mt-5 leading-relaxed">
            SAEBIT / SKIN reads what's happening, conditions your skin for where it is now, and tracks what changes over time. Think of it as the <span className="font-semibold text-ink">gym for your skin</span>, you train your body to keep it strong; here we condition your skin to keep it healthy. Read it, train it, track it, adapt.
          </p>
          <p className="text-lg text-mutedink max-w-[48ch] mt-5 leading-relaxed" data-testid="story-empathy">
            And if you've ever turned off your camera because of your skin, or heard "it's just puberty, it'll pass" one too many times, this place was built for you. Nobody here judges what you did or didn't do. We read what your skin needs, and help you train it.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const STEPS = [
  { n: "01", h: "Identify", sub: "90-Second Skin Assessment", p: "Help us understand your concerns, habits and goals." },
  { n: "02", h: "See it", tag: "In studio", sub: "Skin Passport", p: "Using professional skin imaging, your skin coach measures hydration, oil balance, pigmentation, pores and more. This becomes your baseline, just like a fitness assessment at the gym: and the read is yours to keep." },
  { n: "03", h: "Understand", tag: "In studio", sub: "Your Personal Skin Profile", p: "A real conversation, not a sales pitch: your coach asks about sleep, sun, sport, stress, what you eat and the products you brought along: because skin is whole-body. Then we connect it to what the scan shows and what your skin needs most." },
  { n: "04", h: "Train", tag: "In studio", sub: "Your First Skin Training Session", p: "Based on your assessment and scan, we run the session that's right for your skin today. You leave with your Skin Training Plan: treatments, a simple routine, and what to change at home." },
  { n: "05", h: "Check in", tag: "Every month", sub: "Re-scan, read against your last visit", p: "Each monthly session starts with a JANUS re-scan read against your previous one. Progress gets measured, not guessed, and your plan is fine-tuned to keep your skin moving in the right direction." },
  { n: "06", h: "Maintain", tag: "Ongoing", sub: "Goal reached: now keep it", p: "Healthy skin is a habit, not a finish line. Once you hit your goal, the record shows exactly what got you there, so maintenance is lighter and you never start from zero again." },
];

function HowItWorks() {
  return (
    <section id="how" className="bg-ink text-cream py-16 sm:py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal>
          <Eyebrow light>Your journey at saebit</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.035em] leading-[0.98] text-cream text-4xl md:text-[3.4rem] mb-5">
            Identify. See it. <span className="serif-it text-sage">Understand. Train.</span><br className="hidden md:block" /> Then check in, and keep it.
          </h2>
          <p className="text-lg text-cream/70 max-w-[58ch] mb-12 leading-relaxed" data-testid="journey-intro">
            Whether it's exam season breakouts, prom in three months, first-job interviews or skin that flares every football season, the path is the same: measure, train, re-measure. Your first two steps happen in one visit. Steps five and six are what membership is for.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="journey-steps">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className={`border-t-[3px] pt-5 h-full ${i >= 4 ? "border-coral" : "border-sage"}`} data-testid={`journey-step-${s.n}`}>
                <div className="flex items-start justify-between mb-4">
                  <span className={`block font-disp font-extrabold text-5xl leading-none ${i >= 4 ? "text-coral" : "text-sage"}`}>{s.n}</span>
                  {s.tag ? (
                    <span className={`text-[11px] font-bold uppercase tracking-[0.14em] rounded-full px-2.5 py-1 ${i >= 4 ? "text-cream bg-coral" : "text-ink bg-sage"}`}>{s.tag}</span>
                  ) : null}
                </div>
                <h3 className="font-disp font-bold text-2xl text-cream mb-1">{s.h}</h3>
                <p className={`text-sm font-semibold uppercase tracking-[0.08em] mb-2.5 ${i >= 4 ? "text-coral" : "text-sage"}`}>{s.sub}</p>
                <p className="text-cream/70 leading-relaxed">{s.p}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
            <p className="text-lg text-cream/70 max-w-[58ch] leading-relaxed">
              We condition skin: train it session by session, measure the progress, and adjust the program. The session is how we act on the data; the record is what makes it compound.
            </p>
            <a href="/membership" className="group inline-flex flex-none items-center gap-2 bg-coral text-cream font-semibold px-6 py-3 rounded-full border-2 border-cream/80 shadow-[3px_3px_0_rgba(247,242,231,0.35)] hover:-translate-y-[2px] transition-all" data-testid="journey-membership-cta">
              See membership <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Passport() {
  const [open, setOpen] = useState(false);
  return (
    <section id="passport" className="py-16 sm:py-24 md:py-32" style={{ background: "linear-gradient(165deg,#F5E8E3,#F0D6CE)" }}>
      <PassportModal open={open} onClose={() => setOpen(false)} />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Eyebrow>The core: your Skin Passport</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.035em] leading-[0.98] text-ink text-4xl md:text-[3.4rem] mb-5">
            Your skin,<br />on the record.
          </h2>
          <p className="text-lg text-mutedink max-w-[42ch] leading-relaxed mb-4">
            This is the actual product. Every visit we measure your skin and update one living record (keep, stop, add) that grows with you. The facial is how we condition; <strong className="text-ink font-semibold">the passport is what you own</strong>.
          </p>
          <p className="serif-it text-lg text-ink/85 max-w-[42ch] leading-relaxed mb-6" data-testid="passport-routine-hook">
            "How do I build my skincare routine?" is the question we hear most. This is the answer: <span className="font-semibold not-italic">keep, stop, add</span>. Your routine, built and rebuilt from measurements, not TikTok.
          </p>
          <ul className="space-y-3 mb-8">
            {["A measured baseline at your first scan", "Read against your last visit, never from zero", "Plain words, by ingredient, not brand"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-ink font-medium">
                <span className="w-5 h-5 rounded-full bg-coral border-2 border-ink flex-none" /> {t}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="group inline-flex items-center gap-2 bg-ink text-cream font-semibold text-lg px-7 py-4 rounded-full border-2 border-ink shadow-[4px_4px_0_#D48F7D] hover:-translate-y-[2px] hover:shadow-[7px_7px_0_#D48F7D] active:translate-y-0 active:shadow-none transition-all"
            data-testid="passport-preview-button"
          >
            Preview a real passport
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </Reveal>

        <Reveal delay={0.1}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-left w-full bg-ink text-cream rounded-[1.6rem] border-[2.5px] border-ink shadow-[11px_11px_0_rgba(61,43,36,0.22)] p-7 max-w-[470px] mx-auto block hover:-translate-y-1 transition-transform cursor-pointer"
            data-testid="passport-card"
          >
            <div className="flex justify-between text-[0.66rem] font-bold uppercase tracking-[0.18em] text-sage mb-5">
              <span>Skin Passport</span><span className="text-cream/55">#0042 · Member</span>
            </div>
            <div className="font-disp font-bold text-2xl leading-[1.05] mb-1">Identity: <span className="serif-it text-dawn">Pore Clarity</span></div>
            <span className="block font-accent italic text-cream/50 mb-6">First priority: keep pores clear, gently</span>
            <div className="space-y-4 border-t border-cream/15 pt-5">
              {[
                ["KEEP", "bg-sage/30 text-sage", <>Your gentle gel cleanser. <strong className="text-white font-semibold">It isn't stripping your barrier.</strong></>],
                ["STOP", "bg-coral/30 text-dawn", <>Switching actives every week. <strong className="text-white font-semibold">Give them 6 weeks.</strong></>],
                ["ADD", "bg-cream/15 text-cream", <>Daily SPF. <strong className="text-white font-semibold">Your #1 lever</strong> for marks that linger.</>],
              ].map(([tag, cls, body]) => (
                <div key={tag} className="flex gap-3.5 items-start">
                  <span className={`flex-none font-bold text-[0.6rem] tracking-wide px-2 py-1.5 rounded-md ${cls}`}>{tag}</span>
                  <p className="text-sm text-cream/85 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 text-xs italic text-cream/50">Measured at your first session · grows every visit</div>
            <div className="mt-3 text-sm font-semibold text-sage">Tap to open the full passport →</div>
          </button>
        </Reveal>
      </div>
    </section>
  );
}

const DIFFS = [
  ["A facial that relaxes you for the weekend.", "A protocol that ", "improves your skin", " by next month."],
  ["Ask the obvious: “what's your skin concern?”", "Read the actual ", "pattern", " behind it."],
  ["Read your skin through one therapist's eyes, a different verdict each visit.", "An engine + a coach, reading it ", "the same way", " every time."],
  ["Squeeze congestion out by force.", "Clear pores ", "gently", ", tool-led, no squeezing, no marks."],
  ["Sell you 10 sessions upfront, no proof they work.", "We measure if it's ", "actually working", ", every visit."],
  ["Treat the breakout in front of them.", "Condition the skin so it ", "stops coming back", "."],
  ["“Korean-inspired”, on generic machines.", "Machines and skincare ", "imported from Korea", ", the actual system."],
  ["Gen X protocols, scaled down.", "Built for skin that's still ", "forming", "."],
  ["Start from zero every time.", "Your passport ", "builds", ", visit after visit."],
];

function WhyDifferent() {
  return (
    <section id="why" className="bg-paper py-16 sm:py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <Reveal>
          <Eyebrow>Why saebit hits different</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.035em] leading-[0.98] text-ink text-4xl md:text-[3.4rem] mb-4">
            A clinic treats once.<br />saebit conditions for life.
          </h2>
          <p className="text-lg text-mutedink leading-relaxed mb-8">
            Spas and clinics read your skin too, but through one therapist's eyes, off a menu, with nothing measured or kept. saebit reads it the same way every visit, conditions on the data, and keeps the record, so every visit compounds instead of resetting.
          </p>
        </Reveal>
        <div className="border-t-2 border-ink">
          {DIFFS.map((d, i) => (
            <Reveal key={d[0]} delay={i * 0.04}>
              <div className="grid md:grid-cols-2 gap-5 py-6 border-b-2 border-ink items-center">
                <span className="flex gap-2.5 text-lightink text-base md:text-lg">
                  <XIcon size={18} className="text-coral flex-none mt-1" strokeWidth={3} />
                  {d[0]}
                </span>
                <span className="font-disp font-bold text-xl leading-snug text-ink">
                  {d[1]}<em className="font-accent not-italic italic text-coralDeep font-semibold">{d[2]}</em>{d[3]}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 text-sm text-mutedink">
            The longer version, honestly told:{" "}
            <a href="/blog/how-korean-facials-differ-from-the-rest" className="font-semibold text-ink underline underline-offset-4 decoration-ink/30 hover:text-coral transition-colors" data-testid="why-different-article-link">
              Korean facial vs. the usual facial, it's the approach →
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const KOREAN_PROTOCOLS = [
  ["딥 클렌징", "Deep-cleanse ritual", "The K-studio way to clean at pore level: an oil phase dissolves sunscreen, sebum and buildup deep in the pore, a water phase lifts it all away. City grime gone, without stripping a barrier that's still forming."],
  ["MTS", "Cosmetic microneedling", "Cosmetic-grade MTS at 0.25mm, just deep enough to improve skin appearance, smooth texture and enhance product absorption. Barrier-safe and epidermal-only: no injections, no downtime."],
  ["고무팩", "Cooling modeling mask", "The classic Korean rubber mask. It cools heat-stressed skin, calms active breakouts, and seals the session's hydration in. Gentle enough for teen skin."],
  ["소노·크라이오", "Sono & cryo therapy", "Sono LDM ultrasound soothes the barrier from within, modulating inflammation to calm reactive, breakout-prone skin. Cryo calms surface inflammation and acne redness, and tightens pores."],
  ["아쿠아필", "Aqua peel", "Korea's most-booked deep-pore clean: a gentle water-suction peel lifts blackheads and congestion without squeezing or scraping. Made for oily, breakout-prone young skin."],
  ["LED 테라피", "LED light therapy", "Specific wavelengths penetrate the skin at different depths, triggering natural cellular repair. Blue and red are a dual-action punch: blue destroys acne bacteria (C. acnes), while red takes down inflammation and helps the skin heal. No heat, no downtime."],
];

function KoreanMethod() {
  return (
    <section id="korean-method" className="bg-cream py-16 sm:py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>The Korean approach</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.04em] leading-[1.02] text-ink text-3xl sm:text-4xl md:text-5xl max-w-2xl">
            Korean skin-conditioning protocols, <span className="serif-it text-coralDeep">rebuilt for young tropical skin.</span>
          </h2>
          <p className="mt-5 text-mutedink leading-relaxed max-w-2xl">
            Korea treats skin the way athletes treat training: consistent, preventive, measured. Prevention over cure.
            Our protocols are adapted from Mikwang Academy in Seoul, where hundreds of clients have trained their skin
            through this same structured, measured approach. saebit rebuilds them for skin that's still forming, and for
            heat and humidity Seoul never planned for. The machines and skincare are imported from Korea, the actual
            professional system, not "Korean-inspired." But this isn't a menu you order from. These are the tools. Your
            skin decides which ones get used:
          </p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {KOREAN_PROTOCOLS.map(([kr, title, body], i) => (
            <Reveal key={title} delay={0.06 * i}>
              <div className="h-full bg-paper border-2 border-ink rounded-2xl p-5 shadow-[4px_4px_0_#3D2B24]" data-testid={`korean-protocol-${i}`}>
                <div className="flex items-baseline gap-2.5 mb-2">
                  <span className="font-disp font-extrabold text-lg text-clay break-keep">{kr}</span>
                  <h3 className="font-disp font-bold text-lg text-ink">{title}</h3>
                </div>
                <p className="text-sm text-mutedink leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-8 bg-ink text-cream border-2 border-ink rounded-2xl p-6 sm:p-7 shadow-[5px_5px_0_rgba(61,43,36,0.3)] sm:flex sm:items-center sm:justify-between sm:gap-6" data-testid="korean-method-note">
            <div>
              <div className="text-[0.66rem] font-bold uppercase tracking-[0.16em] text-sage mb-1.5">Prioritised to your skin</div>
              <p className="font-disp font-bold text-lg sm:text-xl leading-snug mb-1.5">We don't sell these à la carte.</p>
              <p className="text-sm text-cream/75 leading-relaxed max-w-xl">
                Your 90-second assessment and JANUS scan set one clear priority, and your session is built from these protocols around it. Different skin, different combination, every visit. Non-invasive, no injections, no lasers, no medical claims.
              </p>
            </div>
            <a
              href="/quiz"
              className="group mt-4 sm:mt-0 flex-none inline-flex items-center justify-center gap-2 bg-clay text-white font-semibold px-6 py-3 rounded-full border-2 border-cream/80 shadow-[3px_3px_0_rgba(247,242,231,0.3)] hover:-translate-y-[2px] transition-all"
              data-testid="korean-method-quiz-cta"
            >
              Find your priority <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const SKINMAXX_POINTS = [
  ["A training room, not a spa", "Private 1:1 room, no robes, no rituals, no candle budget. You're in, your skin gets trained, you're out in 75 minutes."],
  ["Numbers, not vibes", "The JANUS scan scores your oil, hydration, pores and texture. You leave with a baseline and one priority, the same way a gym assessment works."],
  ["No unnecessary products", "Guidance by ingredient, by need, no product wall, no upsell, nothing pushed across a counter."],
];

function Skinmaxxing() {
  return (
    <section id="skinmaxxing" className="bg-ink text-cream py-16 sm:py-24 md:py-32" data-testid="skinmaxxing-section">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        <Reveal>
          <Eyebrow light>For the guys too</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.035em] leading-[0.98] text-cream text-4xl md:text-[3.4rem] mb-5">
            Skinmaxxing, <span className="serif-it text-sage">but measured.</span>
          </h2>
          <p className="text-lg text-cream/75 max-w-[54ch] leading-relaxed">
            Half the skinmaxxing wave is guys, and most of them would rather quit than walk into a spa. Fair. saebit isn't one. It's closer to a gym with a scanner: you train your skin on data, not products stacked off a For You page.
          </p>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-6 lg:gap-8">
          {SKINMAXX_POINTS.map(([h, p], i) => (
            <Reveal key={h} delay={i * 0.07}>
              <div className="border-t-[3px] border-sage pt-5 h-full" data-testid={`skinmaxxing-point-${i}`}>
                <h3 className="font-disp font-bold text-xl text-cream mb-2">{h}</h3>
                <p className="text-cream/70 leading-relaxed text-[15px]">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.12}>
          <div className="mt-11 flex flex-col sm:flex-row sm:items-center gap-4">
            <a
              href="/quiz"
              className="group inline-flex items-center justify-center gap-2 bg-clay text-white font-semibold text-lg px-7 py-4 rounded-full border-2 border-cream/80 shadow-[3px_3px_0_rgba(247,242,231,0.3)] hover:-translate-y-[2px] transition-all self-start"
              data-testid="skinmaxxing-cta"
            >
              Get my baseline <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <span className="text-sm text-cream/55">Start where every program starts: the baseline. 90 seconds, free.</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Community() {
  const EVENTS = ["Skin workshops", "Student nights", "Small talks", "Community sessions"];
  return (
    <section id="community" className="bg-paper border-y-[2.5px] border-ink py-16 sm:py-24" data-testid="community-section">
      <div className="max-w-4xl mx-auto px-6 sm:px-8">
        <Reveal>
          <Eyebrow>Community</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.03em] leading-[1.02] text-ink text-3xl md:text-[2.6rem] mb-5">
            More than <span className="serif-it text-coralDeep">appointments.</span>
          </h2>
          <p className="text-lg text-mutedink max-w-[54ch] leading-relaxed">
            Saebit is a place to figure things out together. Skin changes. Bodies change. And most of us end up learning what to do from TikTok, Reddit, friends and trial and error. So we're making room for conversations too.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            {EVENTS.map((e) => (
              <span key={e} className="bg-cream border-2 border-ink rounded-full px-3.5 py-1 text-sm font-semibold text-ink">{e}</span>
            ))}
          </div>
          <p className="mt-6 text-sm text-mutedink">
            <a href="https://wa.me/60123502721?text=Hi%20saebit!%20What's%20happening%20at%20the%20studio%3F" target="_blank" rel="noopener noreferrer nofollow" className="font-semibold text-ink underline underline-offset-4 decoration-coral decoration-2 hover:text-coralDeep transition-colors" data-testid="community-cta">
              See what's happening at Saebit →
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function MotherBrand() {
  return (
    <section className="bg-cream py-16 sm:py-20" data-testid="motherbrand-section">
      <div className="max-w-2xl mx-auto px-6 sm:px-8 text-center">
        <p className="text-[0.65rem] font-bold uppercase tracking-[0.22em] text-mutedink mb-5">Saebit started with skin</p>
        <p className="font-disp font-bold tracking-[-0.02em] leading-snug text-ink text-xl md:text-2xl mb-5">
          Our bodies don't stay the same.
        </p>
        <p className="text-mutedink leading-relaxed mb-4">
          Puberty. Stress. Hormones. Age. Environment. Life. What worked before doesn't always work now. We started Saebit with young skin because it's one of the first times you really notice your body changing. We're building from there.
        </p>
        <p className="text-ink font-semibold">Understand what's changing. Then decide what it needs.</p>
        <p className="font-disp font-extrabold tracking-[0.14em] text-ink mt-6">SAEBIT</p>
      </div>
    </section>
  );
}

const PERKS = [
  ["01", "Scan + Skin Passport included", "Members get the JANUS Pro scan and a Skin Passport update free at every visit. Non-members pay RM50 per scan."],
  ["02", "One monthly 1:1 session", "A private Skin Training Session every month, built from your scan, you and your coach, no group, no performing."],
  ["03", "Students: 10% off", "RM251/month with a valid student ID, shown at your first visit."],
  ["04", "No contract, no packages", "Pause for a month up to twice a year, cancel anytime with one month's notice. Saturdays from 10 October 2026."],
];

function Founding() {
  return (
    <section id="founding" className="bg-clay text-white py-16 sm:py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <Reveal>
          <Sticker dark className="!bg-ink !text-cream"><Star size={14} fill="#B2C5B2" stroke="#B2C5B2" /> First visit 20% off</Sticker>
          <h2 className="font-disp font-extrabold tracking-[-0.035em] leading-[0.98] text-white text-4xl md:text-[3.4rem] mt-6 mb-5">
            Try it once.<br />Then make it a record.
          </h2>
          <p className="text-lg text-white/90 max-w-[56ch] leading-relaxed mb-8">
            Our skin studio opens in Bandar Sunway on 10 October 2026, every Saturday. Your first Skin Training Session is 20% off, RM240 instead of RM300, paid online when you book. Membership is how the record starts compounding: a monthly session with your scan and Skin Passport included, free.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-white/80 mb-1">Two ways in</div>
          <p className="text-sm text-white/85 mb-4 max-w-[52ch]">A single visit is a snapshot. Membership is the record, and a record over time only exists if you keep coming back.</p>
          <div className="grid sm:grid-cols-2 gap-4 mb-4 items-start">
            <div className="rounded-2xl p-5 border-2 border-white/60" data-testid="price-firstvisit">
              <div className="text-[0.66rem] font-bold uppercase tracking-[0.16em] text-white/85 mb-2">First visit · 20% off</div>
              <div className="font-disp font-extrabold text-4xl leading-none text-white">RM240 <span className="text-base font-sans font-semibold text-white/60 line-through">RM300</span></div>
              <div className="text-sm text-white/80 mt-2">A 60-minute private 1:1 Skin Training Session. Add the JANUS Pro scan + Skin Passport for RM50. Walk-ins after that are RM300 (scan RM50 extra).</div>
              <a
                href="/book"
                className="group mt-4 w-full inline-flex items-center justify-center gap-2 bg-ink text-cream font-semibold px-5 py-3 rounded-full border-2 border-ink shadow-[3px_3px_0_rgba(61,43,36,0.35)] hover:-translate-y-[2px] transition-all"
                data-testid="landing-book-firstvisit-btn"
              >
                Book my first visit <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            <div className="rounded-2xl p-5 bg-ink text-cream border-2 border-ink shadow-[5px_5px_0_rgba(61,43,36,0.35)]" data-testid="price-member">
              <div className="text-[0.66rem] font-bold uppercase tracking-[0.16em] text-sage mb-2">Membership · the record</div>
              <div className="font-disp font-extrabold text-4xl leading-none">RM279<span className="font-sans font-semibold text-base">/mo</span></div>
              <div className="text-sm text-cream/75 mt-2">A monthly 1:1 session with your JANUS scan and Skin Passport update included, free. Students 10% off. The Passport that compounds every visit, what a walk-in structurally can't give you.</div>
              <MembershipCTA
                testId="landing-join-member-btn"
                className="group mt-4 w-full inline-flex items-center justify-center gap-2 bg-clay text-white font-semibold px-5 py-3 rounded-full border-2 border-cream/80 shadow-[3px_3px_0_rgba(247,242,231,0.35)] hover:-translate-y-[2px] transition-all"
              />
            </div>
          </div>
          <div className="mb-5 flex items-start gap-2.5 bg-ink text-cream rounded-xl px-4 py-3 border-2 border-ink shadow-[4px_4px_0_rgba(61,43,36,0.35)]" data-testid="walkin-credit">
            <ArrowRight size={16} className="text-sage flex-none mt-1 -rotate-45" />
            <p className="text-sm leading-relaxed"><strong className="font-semibold">No-lose first visit:</strong> what you pay for your first visit covers your first month if you join within 7 days, so trying once never costs you extra. It's the on-ramp from snapshot to record.</p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mb-10">
          {PERKS.map((p, i) => (
            <Reveal key={p[0]} delay={i * 0.06}>
              <div className="grid grid-cols-[auto_1fr] gap-3.5 border-t-2 border-white/35 pt-3.5">
                <span className="font-disp font-extrabold text-xl text-white leading-none">{p[0]}</span>
                <div>
                  <b className="block font-semibold text-white mb-1">{p[1]}</b>
                  <p className="text-sm text-white/85 leading-relaxed">{p[2]}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="italic text-white/85 max-w-[48ch] mb-6">
            We open in Bandar Sunway on 10 October 2026, every Saturday. Book your first visit now, Stripe holds your slot the moment you pay.
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
            <a
              href="/book"
              className="group inline-flex items-center justify-center gap-2 bg-ink text-cream font-semibold text-lg px-7 py-4 rounded-full border-2 border-ink shadow-[4px_4px_0_#3D2B24] hover:-translate-y-[2px] transition-all"
              data-testid="founding-cta"
            >
              Book my first visit, RM240 <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/membership"
              className="group inline-flex items-center justify-center gap-2 bg-transparent text-white font-semibold text-lg px-7 py-4 rounded-full border-2 border-white hover:bg-white hover:text-coral transition-all"
              data-testid="founding-membership-cta"
            >
              See how membership works <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
          <p className="mt-5 text-sm text-white/85">
            Questions first?{" "}
            <a
              href="https://wa.me/60123502721?text=Hi%20saebit!%20I%20have%20a%20question."
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="font-semibold text-white underline underline-offset-4 decoration-white/40 hover:text-cream transition-colors"
              data-testid="founding-whatsapp-human"
            >
              WhatsApp a human
            </a>{" "}
, a real person replies.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const PROMISES = [
  ["No products pushed", "Your plan comes first. What you already own comes first. We recommend by need, not by what's sitting on a shelf."],
  ["No packages to push", "We don't sell 10 sessions upfront. We measure whether it's working, every visit."],
  ["A doctor when you need one", "If your skin needs medical care, we say so and refer you out, it never goes on a menu."],
  ["Young skin, handled by stage", "Under-18s join with parent consent, and actives are introduced by skin stage, never by trend."],
  ["Zero judgment", "Come with makeup or without, mid-breakout or not. Nobody here lectures you about what you \u2018should have\u2019 done."],
  ["Sensitive skin, read first", "Reactive, redness-prone, stings-at-everything skin? The scan reads your sensitivity before anyone touches your face, and every protocol has a gentler variant."],
];

function Promises() {
  return (
    <section id="promises" className="bg-cream py-16 sm:py-24" data-testid="promises-strip">
      <div className="max-w-4xl mx-auto px-6 sm:px-8">
        <Reveal>
          <Eyebrow>Our promises</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.03em] leading-[1.02] text-ink text-3xl md:text-[2.6rem] mb-4">
            Fair <span className="serif-it text-coralDeep">by design.</span>
          </h2>
          <p className="text-lg text-mutedink max-w-[52ch] leading-relaxed mb-9">
            You never pay for a studio that isn't open, a session that didn't happen, or a membership you've outgrown. And a few more things we simply won't do:
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-4">
          {PROMISES.map(([h, p], i) => (
            <Reveal key={h} delay={i * 0.05}>
              <div className="bg-paper border-2 border-ink rounded-2xl p-5 h-full" data-testid={`promise-${i}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <HeartHandshake size={16} className="text-coralDeep" strokeWidth={2.4} />
                  <h3 className="font-disp font-bold text-ink">{h}</h3>
                </div>
                <p className="text-sm text-mutedink leading-relaxed">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="flex-none">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const FAQS = FAQS_DATA.map(({ q, a, link }) => [
  q,
  <>
    {a}
    {link && (
      <>
        {" "}
        <a href={link.href} className="font-medium underline decoration-coral/60 underline-offset-2 hover:text-coral" data-testid={link.testId}>
          {link.label} →
        </a>
      </>
    )}
  </>,
]);

function Faq() {
  return (
    <section id="faq" className="bg-cream py-16 sm:py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <Reveal>
          <p className="text-center"><Eyebrow>Questions, answered straight</Eyebrow></p>
        </Reveal>
        <div className="space-y-4 mt-6">
          {FAQS.map((f, i) => (
            <Reveal key={f[0]} delay={i * 0.03}>
              <details className="faq-item bg-paper border-[2.5px] border-ink rounded-2xl shadow-[4px_4px_0_#3D2B24] overflow-hidden" data-testid={`faq-item-${i}`}>
                <summary className="flex items-center justify-between gap-4 cursor-pointer px-6 py-5 font-disp font-bold text-lg text-ink select-none">
                  {f[0]}
                  <Plus size={22} className="faq-icon text-coral flex-none" strokeWidth={2.5} />
                </summary>
                <div className="px-6 pb-6 text-mutedink leading-relaxed">{f[1]}</div>
              </details>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ParentsBand() {
  return (
    <section className="bg-cream pb-16 sm:pb-24" data-testid="parents-band">
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <div className="bg-paper border-[2.5px] border-ink rounded-2xl shadow-[4px_4px_0_#3D2B24] px-6 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex-1">
            <p className="font-disp font-bold text-ink text-lg">Parent of a teen?</p>
            <p className="text-mutedink text-sm leading-relaxed mt-1.5">
              Everything here is non-invasive and age-gated. Under-18s join with your consent, actives are introduced by skin stage, not by trend, and anything that looks medical gets referred to a doctor, not put on a menu.
            </p>
          </div>
          <a
            href="/standards"
            className="group inline-flex items-center gap-1.5 flex-none self-start sm:self-center font-semibold text-sm text-ink underline underline-offset-4 decoration-2 decoration-coral hover:text-coralDeep transition-colors"
            data-testid="parents-band-link"
          >
            Read our safety standards <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
}

function WaitlistSection() {
  return (
    <section id="waitlist" className="bg-sage py-16 sm:py-24 md:py-32">
      <div className="max-w-2xl mx-auto px-6 sm:px-8 text-center">
        <Reveal>
          <Eyebrow>Start here</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.035em] leading-[0.98] text-ink text-4xl md:text-[3.4rem] mb-4">
            Your record<br />starts with the assessment.
          </h2>
          <p className="text-lg text-dusk max-w-md mx-auto leading-relaxed mb-9">
            Take the 90-second Skin Assessment, leave your email at the end, and your Skin Passport begins from that first read. Ready to go further? Book your first visit, every Saturday from 10 October 2026.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <a
            href="/quiz"
            className="group inline-flex items-center gap-2 bg-clay text-white font-semibold text-lg px-8 py-4 rounded-full border-2 border-ink shadow-[5px_5px_0_#3D2B24] hover:-translate-y-[2px] hover:shadow-[7px_7px_0_#3D2B24] active:translate-y-0 active:shadow-none transition-all"
            data-testid="waitlist-quiz-cta"
          >
            Skin Assessment
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <p className="mt-5 text-sm text-dusk/80">The assessment takes 90 seconds · Free · Your record is yours, deletable anytime.</p>
        </Reveal>
      </div>
    </section>
  );
}

function VisitUs() {
  const ADDRESS = "10-1, Jalan PJS 11/28A, Bandar Sunway, 47500 Subang Jaya, Selangor, Malaysia";
  const Q = encodeURIComponent(ADDRESS);
  return (
    <section id="visit" className="bg-paper py-16 sm:py-24 md:py-32" data-testid="visit-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <Reveal>
          <Eyebrow>Visit us</Eyebrow>
          <h2 className="font-disp font-extrabold tracking-[-0.035em] leading-[0.98] text-ink text-4xl md:text-[3.4rem] mb-5">
            Find us in<br /><span className="serif-it text-coralDeep">Bandar Sunway.</span>
          </h2>
          <div className="inline-flex items-center gap-2 bg-sage text-ink font-bold text-xs uppercase tracking-wide px-4 py-2 rounded-full border-2 border-ink mb-6">
            <Star size={13} fill="#3D2B24" stroke="#3D2B24" /> Opening 10 October 2026 · Saturdays
          </div>
          <div className="flex items-start gap-3 text-mutedink text-lg leading-relaxed mb-6 max-w-[38ch]">
            <MapPin size={22} className="text-coral flex-none mt-1" strokeWidth={2.4} />
            <p data-testid="visit-address">
              10-1 Jalan PJS 11/28A, Bandar Sunway<br />
              47500 Subang Jaya, Selangor, Malaysia
            </p>
          </div>
          <p className="text-mutedink leading-relaxed max-w-[48ch] mb-3" data-testid="visit-campus-note">
            Studying nearby? Our skin studio sits on one of the densest student stretches in Malaysia, a short walk or one Grab from campus:
          </p>
          <div className="flex flex-wrap gap-2 mb-7" data-testid="visit-campuses">
            {CAMPUSES.map((c) => (
              <span key={c} className="bg-cream border-2 border-ink rounded-full px-3.5 py-1 text-sm font-semibold text-ink" data-testid="campus-chip">
                {c}
              </span>
            ))}
          </div>
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${Q}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 bg-ink text-cream font-semibold text-lg px-7 py-4 rounded-full border-2 border-ink shadow-[4px_4px_0_#D48F7D] hover:-translate-y-[2px] hover:shadow-[7px_7px_0_#D48F7D] active:translate-y-0 active:shadow-none transition-all"
            data-testid="visit-directions"
          >
            Get directions
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </Reveal>
        <Reveal delay={0.1}>
          <iframe
            title="Saebit Skin location, Bandar Sunway"
            src={`https://maps.google.com/maps?q=${Q}&z=17&output=embed`}
            className="w-full h-[320px] sm:h-[400px] rounded-[18px] border-[2.5px] border-ink shadow-[8px_8px_0_#3D2B24]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="visit-map"
          />
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-ink text-cream/70 pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <p className="max-w-[60ch] text-cream/80 leading-relaxed">
          A starting point, not a diagnosis. We've built a tracking system for young skin: measured, recorded, and improving with every scan, with a coach in the room to condition it.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <a href="#top" className="font-disp font-extrabold text-2xl tracking-[-0.04em] text-cream"><span className="text-coral">sae</span>bit</a>
            <div className="mt-1 text-sm">Opening 10 October 2026 · Saturdays · Malaysia</div>
            <div className="mt-1 text-sm">Enquiries: <a href="mailto:hello@saebit.ai" className="text-cream/85 hover:text-sage">hello@saebit.ai</a> <span className="text-cream/60">or</span> <a href="https://wa.me/60123502721?text=Hi%20saebit!%20I%20have%20a%20question." target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-cream/85 hover:text-sage" data-testid="footer-whatsapp"><WhatsAppIcon size={13} /> WhatsApp</a></div>
            <div className="mt-2 flex gap-4 text-sm">
              <a href="https://www.instagram.com/saebit.ai" target="_blank" rel="noopener me" className="text-cream/85 hover:text-sage" data-testid="footer-instagram-link">Instagram</a>
              <a href="https://www.threads.com/@saebit.ai" target="_blank" rel="noopener me" className="text-cream/85 hover:text-sage" data-testid="footer-threads-link">Threads</a>
            </div>
          </div>
          <div className="flex flex-wrap gap-5 font-medium text-cream/85">
            <a href="/membership" className="hover:text-sage">Membership</a>
            <a href="/skin/koreanfacial" className="hover:text-sage">Korean Facial</a>
            <a href="/standards" className="hover:text-sage">Safety &amp; Care</a>
            <a href="/blog" className="hover:text-sage">Blog</a>
            <a href="/book" className="hover:text-sage" data-testid="footer-book-link">Book a session</a>
            <a href="/quiz" className="hover:text-sage">Skin Assessment</a>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-cream/12 text-xs text-cream/60 leading-relaxed space-y-2">
          <p>© 2026 Saebit Skin Sdn Bhd. All rights reserved.</p>
          <p>Saegyeol™ and Skin Passport™ are trademarks of Saebit Skin Sdn Bhd. saebit is a skin conditioning studio and does not provide medical diagnosis. Information shared here is educational and not a substitute for professional medical advice.</p>
        </div>
      </div>
    </footer>
  );
}

function StickyQuizBar() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 bg-cream/85 backdrop-blur-md border-t-2 border-ink transition-all duration-300 ${
        show ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
      }`}
      data-testid="sticky-quiz-bar"
    >
      <a
        href="/quiz"
        className="group flex items-center justify-center gap-2 w-full bg-clay text-white font-semibold text-base whitespace-nowrap px-6 py-3.5 rounded-full border-2 border-ink shadow-[3px_3px_0_#3D2B24] active:translate-y-[1px] active:shadow-none transition-all"
        data-testid="sticky-quiz-cta"
      >
        Skin Assessment
        <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
      </a>
    </div>
  );
}

export default function Landing() {
  // Nav links like /#passport land here before lazy content mounts, retry
  // until the target section exists, then scroll (offset for the fixed nav).
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash || hash === "top") return;
      let tries = 0;
      const attempt = () => {
        const el = document.getElementById(hash);
        if (el) {
          window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 84, behavior: "smooth" });
          return;
        }
        if (++tries < 60) requestAnimationFrame(attempt);
      };
      attempt();
    };
    const t = setTimeout(scrollToHash, 100);
    window.addEventListener("hashchange", scrollToHash);
    return () => {
      clearTimeout(t);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, []);

  return (
    <div className="bg-cream" data-testid="landing-page">
      <Seo
        title="SAEBIT, Skin Conditioning Studio for Young Skin in Malaysia"
        description="Saebit Skin is Malaysia's first skin conditioning studio for young skin: Korean skin-conditioning protocols rebuilt for teens and young adults in tropical heat. Start with the 90-second Skin Assessment."
        path="/"
      />
      <Nav />
      <Hero />
      <Ribbon />
      <Story />
      <HowItWorks />
      <Passport />
      <WhyDifferent />
      <KoreanMethod />
      <Skinmaxxing />
      <Community />
      <Founding />
      <Promises />
      <MotherBrand />
      <Faq />
      <ParentsBand />
      <WaitlistSection />
      <VisitUs />
      <Footer />
      <StickyQuizBar />
    </div>
  );
}
